import type { IAction, IActionParameter, IDisplayItem, IEvent, ISymbol } from "@kuika/kuika-cl-model";
import { getDesignerNestedActionCollections } from "../../designer-action-context-actions";
import type { ScreenDesignInstance } from "../../designer-context";
import { ScreenDesignHelper } from "../../screen-design-helper";

/**
 * KOA-18206 — screen-level action instances must not outlive the component
 * they were created for.
 *
 * A `datasource="X"` attribute in `ui_render` does not just write a property
 * symbol: when the symbol has no `resultActionId` yet, `ui_logic_set_datasource_bindings`
 * bootstraps one by attaching X to the SCREEN ROOT's `pageInit` event
 * (`property-tools.ts`, "race-free instance bootstrap"). That instance lives on
 * display item `-1`, not on the component that asked for it.
 *
 * `ui_component.delete` splices the component's `IDisplayItem` subtree out of the
 * tree, which implicitly removes every action attached to the deleted components
 * themselves — but the bootstrapped `pageInit` instance is not in that subtree,
 * so it survives with its required parameters unbound. Measured (telemetry
 * session e468e015): the Designer landed three charts bound to
 * `GetRiskDashboardData`, then deleted all 11 top-level Rows on DashboardScreen.
 * The screen went completely empty, and `project_validation` still reported four
 * blocking `KV1170 "The required parameter is not set"` errors on a `pageInit`
 * action instance nothing on the screen could trigger any more.
 *
 * The sweep below is deliberately narrow. It only removes an instance when BOTH
 * hold:
 *   1. a component deleted in this operation actually referenced it, and
 *   2. nothing left anywhere on the screen references it any more.
 * So a `pageInit` load the user added independently, or one another surviving
 * component (or another action's parameter) still reads from, is never touched.
 */

const SCREEN_ROOT_KUIKA_ID: string = "-1";

export interface OrphanScreenActionSweepResult {
  /** Ids of the screen-level action instances actually removed. */
  removedActionIds: string[];
}

/**
 * The action instances a single symbol points at. `resultActionId` is the
 * datasource/actionResult link; `parentActionId` is how an `actionField` symbol
 * names the instance whose row it reads.
 */
function collectSymbolActionRefs(symbol: ISymbol | undefined, into: Set<string>): void {
  if (!symbol) return;
  const resultActionId: unknown = symbol.resultActionId;
  if (typeof resultActionId === "string" && resultActionId.length > 0) into.add(resultActionId);
  const parentActionId: unknown = symbol.parentActionId;
  if (typeof parentActionId === "string" && parentActionId.length > 0) into.add(parentActionId);
}

function collectParameterActionRefs(parameters: IActionParameter[] | undefined, into: Set<string>): void {
  for (const parameter of parameters ?? []) {
    collectSymbolActionRefs(parameter?.symbol, into);
    collectParameterActionRefs(parameter?.parameters, into);
  }
}

/** Walks an action collection AND its nested branches (IfThenElse / ForEach / …). */
function collectActionTreeRefs(actions: IAction[] | undefined, into: Set<string>): void {
  for (const action of actions ?? []) {
    collectParameterActionRefs(action?.parameters, into);
    for (const nested of getDesignerNestedActionCollections(action)) {
      collectActionTreeRefs(nested.actions, into);
    }
  }
}

/** Every action instance referenced by one display item's own properties and events. */
function collectDisplayItemActionRefs(di: IDisplayItem | undefined, into: Set<string>): void {
  for (const property of di?.item?.properties ?? []) {
    collectSymbolActionRefs(property?.symbol, into);
  }
  for (const event of di?.item?.events ?? []) {
    collectActionTreeRefs(event?.actions, into);
  }
}

/**
 * Action instances referenced by `di` and everything under it. Call this BEFORE
 * the delete — afterwards the subtree is gone and the references with it.
 */
export function collectSubtreeActionRefs(di: IDisplayItem): Set<string> {
  const refs = new Set<string>();
  collectDisplayItemActionRefs(di, refs);
  for (const descendant of ScreenDesignHelper.getAllChildrenForDI(di)) {
    collectDisplayItemActionRefs(descendant, refs);
  }
  return refs;
}

/**
 * Remove screen-level action instances that the just-deleted components were the
 * last referrers of. `candidateActionIds` is the union of every
 * `collectSubtreeActionRefs` result from this delete operation.
 */
export function sweepOrphanedScreenActions(
  sd: ScreenDesignInstance,
  candidateActionIds: ReadonlySet<string>
): OrphanScreenActionSweepResult {
  const removedActionIds: string[] = [];
  if (candidateActionIds.size === 0) return { removedActionIds };

  const dict: Record<number, IDisplayItem> = sd.screenProvider.state.screenDesignDictionary;
  const rootDi: IDisplayItem | undefined = dict[Number(SCREEN_ROOT_KUIKA_ID)];
  const rootEvents: IEvent[] | undefined = rootDi?.item?.events;
  if (!Array.isArray(rootEvents) || rootEvents.length === 0) return { removedActionIds };

  // What the SURVIVING screen still points at — properties and action parameters
  // alike, across every display item including the root's own event tree.
  const stillReferenced = new Set<string>();
  for (const key of Object.keys(dict)) {
    collectDisplayItemActionRefs(dict[Number(key)], stillReferenced);
  }

  for (const event of rootEvents) {
    const orphans: IAction[] = (event.actions ?? []).filter(
      (action: IAction): boolean => candidateActionIds.has(action.id) && !stillReferenced.has(action.id)
    );
    for (const action of orphans) {
      sd.propertyProvider.deleteActionFromEvent(SCREEN_ROOT_KUIKA_ID, action, event);
      removedActionIds.push(action.id);
    }
  }
  return { removedActionIds };
}
